import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  XMarkIcon, 
  TruckIcon, 
  MapPinIcon, 
  CalendarIcon, 
  ClockIcon,
  UsersIcon,
  PhoneIcon,
  IdentificationIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Trip } from '../../types';
import { DateDisplay } from '../../components/Forms';

interface TripDetailsProps {
  tripId: string;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (trip: Trip) => void;
}

const TripDetails: React.FC<TripDetailsProps> = ({ tripId, isOpen, onClose, onEdit }) => {
  const { data: trip, isLoading, error } = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => apiService.getTrip(tripId),
    enabled: isOpen && !!tripId,
  });

  const formatTime = (date: string | Date) => {
    return new Date(date).toLocaleTimeString('ar-SA-u-nu-latn', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to get trip route info
  const getTripRouteInfo = (trip: Trip) => {
    // If trip has new routes format, use the first active route
    if (trip.routes && trip.routes.length > 0) {
      const activeRoute = trip.routes.find(r => r.isActive) || trip.routes[0];
      return {
        from: activeRoute.from,
        to: activeRoute.to,
        departureDate: activeRoute.dateFrom,
        departureTime: activeRoute.timeFrom,
        arrivalDate: activeRoute.dateTo,
        arrivalTime: activeRoute.timeTo,
        distance: activeRoute.distance
      };
    }
    
    // Fallback to legacy route format
    if (trip.route) {
      return {
        from: trip.route.from,
        to: trip.route.to,
        departureDate: trip.route.departureTime ? new Date(trip.route.departureTime).toISOString().split('T')[0] : '',
        departureTime: trip.route.departureTime ? new Date(trip.route.departureTime).toISOString().slice(11, 16) : '',
        arrivalDate: trip.route.arrivalTime ? new Date(trip.route.arrivalTime).toISOString().split('T')[0] : '',
        arrivalTime: trip.route.arrivalTime ? new Date(trip.route.arrivalTime).toISOString().slice(11, 16) : '',
        distance: trip.route.distance
      };
    }
    
    // Default fallback
    return {
      from: 'غير محدد',
      to: 'غير محدد',
      departureDate: '',
      departureTime: '',
      arrivalDate: '',
      arrivalTime: '',
      distance: undefined
    };
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      'draft': { label: 'مسودة', color: 'bg-gray-100 text-gray-800' },
      'published': { label: 'منشورة', color: 'bg-blue-100 text-blue-800' },
      'full': { label: 'مكتملة العدد', color: 'bg-yellow-100 text-yellow-800' },
      'ongoing': { label: 'جارية', color: 'bg-green-100 text-green-800' },
      'completed': { label: 'مكتملة', color: 'bg-purple-100 text-purple-800' },
      'cancelled': { label: 'ملغية', color: 'bg-red-100 text-red-800' }
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, color: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-center">جاري تحميل تفاصيل الرحلة...</p>
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <XMarkIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">خطأ في تحميل البيانات</h3>
            <p className="mt-2 text-gray-600">لم يتم العثور على تفاصيل الرحلة</p>
            <button
              onClick={onClose}
              className="mt-4 btn-primary"
            >
              إغلاق
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4 space-x-reverse">
            <h2 className="text-xl font-semibold text-gray-900">
              تفاصيل الرحلة {trip.tripNumber}
            </h2>
            {getStatusBadge(trip.status)}
          </div>
          <div className="flex items-center space-x-2 space-x-reverse">
            <button
              onClick={() => onEdit(trip)}
              className="text-green-600 hover:text-green-800 p-2 rounded-lg hover:bg-green-50"
              title="تعديل الرحلة"
            >
              <PencilIcon className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-50"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Trip Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <CalendarIcon className="w-8 h-8 text-blue-600" />
                <div className="mr-3">
                  <p className="text-sm font-medium text-blue-900">تاريخ الرحلة (هجري)</p>
                  <p className="text-lg font-bold text-blue-700">
                    {trip.date ? String(trip.date).split('T')[0] : 'غير محدد'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center">
                <UsersIcon className="w-8 h-8 text-purple-600" />
                <div className="mr-3">
                  <p className="text-sm font-medium text-purple-900">المسافرين</p>
                  <p className="text-lg font-bold text-purple-700">
                    {trip.totalPassengers || 0} / {trip.bus?.capacity || trip.buses?.[0]?.bus?.capacity || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center">
                <TruckIcon className="w-8 h-8 text-orange-600" />
                <div className="mr-3">
                  <p className="text-sm font-medium text-orange-900">الحافلات</p>
                  <p className="text-lg font-bold text-orange-700">
                    {trip.busCount || trip.allBuses?.length || 1} حافلة
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Route Details */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPinIcon className="w-5 h-5 ml-2 text-gray-600" />
              تفاصيل المسارات ({trip.routes?.length || (trip.route ? 1 : 0)})
            </h3>
            
            {/* Multiple Routes */}
            {trip.routes && trip.routes.length > 0 ? (
              <div className="space-y-6">
                {trip.routes.map((route, index) => (
                  <div key={index} className={`border rounded-lg p-4 ${route.isActive ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">
                        المسار {index + 1}
                      </h4>
                      <div className="flex items-center gap-2">
                        {route.isActive && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                            نشط
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">نقطة الانطلاق</label>
                        <p className="text-gray-900 font-medium">{route.from}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الوجهة</label>
                        <p className="text-gray-900 font-medium">{route.to}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">وقت المغادرة</label>
                        <p className="text-gray-900 font-medium">
                          <DateDisplay value={route.dateFrom} className="inline-flex" /> - {route.timeFrom}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">وقت الوصول</label>
                        <p className="text-gray-900 font-medium">
                          {route.dateTo} - {route.timeTo}
                        </p>
                      </div>
                      {route.distance && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">المسافة</label>
                          <p className="text-gray-900 font-medium">{route.distance} كم</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : trip.route ? (
              // Fallback for legacy single route
              <div className="border rounded-lg p-4 border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">المسار الرئيسي</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">نقطة الانطلاق</label>
                    <p className="text-gray-900 font-medium">{trip.route.from}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الوجهة</label>
                    <p className="text-gray-900 font-medium">{trip.route.to}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">وقت المغادرة</label>
                    <p className="text-gray-900 font-medium">
                      {trip.route.departureTime ? String(trip.route.departureTime).split('T')[0] : ''} - 
                      {trip.route.departureTime ? String(trip.route.departureTime).split('T')[1]?.slice(0, 5) : ''}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">وقت الوصول</label>
                    <p className="text-gray-900 font-medium">
                      {trip.route.arrivalTime ? String(trip.route.arrivalTime).split('T')[0] : ''} - 
                      {trip.route.arrivalTime ? String(trip.route.arrivalTime).split('T')[1]?.slice(0, 5) : ''}
                    </p>
                  </div>
                  {trip.route.distance && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">المسافة</label>
                      <p className="text-gray-900 font-medium">{trip.route.distance} كم</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MapPinIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>لا توجد مسارات محددة لهذه الرحلة</p>
              </div>
            )}
          </div>

          {/* Bus Information */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TruckIcon className="w-5 h-5 ml-2 text-gray-600" />
              معلومات الحافلات
            </h3>
            
            {/* Display all buses */}
            {(trip.allBuses && trip.allBuses.length > 0) ? (
              <div className="space-y-4">
                {trip.allBuses.map((busData, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="text-md font-semibold text-gray-800 mb-3">
                      الحافلة {index + 1}: {busData.bus.busNumber}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">رقم الحافلة</label>
                        <p className="text-gray-900 font-medium">{busData.bus.busNumber}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">رقم اللوحة</label>
                        <p className="text-gray-900 font-medium">{busData.bus.plateNumber}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">السعة</label>
                        <p className="text-gray-900 font-medium">{busData.bus.capacity} مقعد</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          (busData.bus.status || 'available') === 'available' ? 'bg-green-100 text-green-800' :
                          busData.bus.status === 'on-trip' ? 'bg-blue-100 text-blue-800' :
                          busData.bus.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {(busData.bus.status || 'available') === 'available' ? 'متاح' :
                           busData.bus.status === 'on-trip' ? 'في رحلة' :
                           busData.bus.status === 'maintenance' ? 'صيانة' : 
                           busData.bus.status === 'out-of-service' ? 'خارج الخدمة' : 
                           'متاح'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Driver for this bus */}
                    {busData.driver && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">السائق:</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500">الاسم</label>
                            <p className="text-sm text-gray-900">{busData.driver.name}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500">رقم الجوال</label>
                            <p className="text-sm text-gray-900">{busData.driver.phone}</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500">رقم الرخصة</label>
                            <p className="text-sm text-gray-900">{busData.driver.licenseNumber}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // Fallback for legacy single bus
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">رقم الحافلة</label>
                  <p className="text-gray-900 font-medium">{trip.bus?.busNumber || trip.buses?.[0]?.bus?.busNumber || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">رقم اللوحة</label>
                  <p className="text-gray-900 font-medium">{trip.bus?.plateNumber || trip.buses?.[0]?.bus?.plateNumber || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">السعة</label>
                  <p className="text-gray-900 font-medium">{trip.bus?.capacity || trip.buses?.[0]?.bus?.capacity || 0} مقعد</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    ((trip.bus?.status || trip.buses?.[0]?.bus?.status) || 'available') === 'available' ? 'bg-green-100 text-green-800' :
                    (trip.bus?.status || trip.buses?.[0]?.bus?.status) === 'on-trip' ? 'bg-blue-100 text-blue-800' :
                    (trip.bus?.status || trip.buses?.[0]?.bus?.status) === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {((trip.bus?.status || trip.buses?.[0]?.bus?.status) || 'available') === 'available' ? 'متاح' :
                     (trip.bus?.status || trip.buses?.[0]?.bus?.status) === 'on-trip' ? 'في رحلة' :
                     (trip.bus?.status || trip.buses?.[0]?.bus?.status) === 'maintenance' ? 'صيانة' : 
                     (trip.bus?.status || trip.buses?.[0]?.bus?.status) === 'out-of-service' ? 'خارج الخدمة' : 
                     'متاح'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Driver Information */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <IdentificationIcon className="w-5 h-5 ml-2 text-gray-600" />
              معلومات السائق
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الاسم</label>
                <p className="text-gray-900 font-medium">{trip.driver?.name || trip.buses?.[0]?.driver?.name || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">رقم الجوال</label>
                <div className="flex items-center">
                  <PhoneIcon className="w-4 h-4 text-gray-400 ml-2" />
                  <p className="text-gray-900 font-medium">{trip.driver?.phone || trip.buses?.[0]?.driver?.phone || '-'}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">رقم الرخصة</label>
                <p className="text-gray-900 font-medium">{trip.driver?.licenseNumber || trip.buses?.[0]?.driver?.licenseNumber || '-'}</p>
              </div>
            </div>
          </div>

          {/* Supervisor Information */}
          {trip.supervisor && (trip.supervisor.name || trip.supervisor.phone) && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UsersIcon className="w-5 h-5 ml-2 text-gray-600" />
                معلومات المشرف
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الاسم</label>
                  <p className="text-gray-900 font-medium">{trip.supervisor.name || 'غير محدد'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">رقم الجوال</label>
                  <div className="flex items-center">
                    <PhoneIcon className="w-4 h-4 text-gray-400 ml-2" />
                    <p className="text-gray-900 font-medium">{trip.supervisor.phone || 'غير محدد'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Financial Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Expenses */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">المصروفات</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">الوقود</span>
                  <span className="font-medium">{trip.expenses.fuel || 0} ر.س</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">الرسوم</span>
                  <span className="font-medium">{trip.expenses.tolls || 0} ر.س</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">الوجبات</span>
                  <span className="font-medium">{trip.expenses.meals || 0} ر.س</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">الإقامة</span>
                  <span className="font-medium">{trip.expenses.accommodation || 0} ر.س</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">الصيانة</span>
                  <span className="font-medium">{trip.expenses.maintenance || 0} ر.س</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">أخرى</span>
                  <span className="font-medium">{trip.expenses.other || 0} ر.س</span>
                </div>
                <div className="border-t pt-3 flex justify-between font-bold">
                  <span>الإجمالي</span>
                  <span>{trip.expenses.total || 0} ر.س</span>
                </div>
              </div>
            </div>

            {/* Revenue */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">الإيرادات</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">مبيعات التذاكر</span>
                  <span className="font-medium">{trip.revenue.ticketSales || 0} ر.س</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">رسوم الإقامة</span>
                  <span className="font-medium">{trip.revenue.accommodationFees || 0} ر.س</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">خدمات إضافية</span>
                  <span className="font-medium">{trip.revenue.additionalServices || 0} ر.س</span>
                </div>
                <div className="border-t pt-3 flex justify-between font-bold">
                  <span>الإجمالي</span>
                  <span>{trip.revenue.total || 0} ر.س</span>
                </div>
                <div className="border-t pt-3 flex justify-between font-bold text-green-600">
                  <span>صافي الربح</span>
                  <span>{(trip.revenue.total || 0) - (trip.expenses.total || 0)} ر.س</span>
                </div>
              </div>
            </div>
          </div>

          {/* Passengers List */}
          {trip.passengers && trip.passengers.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UsersIcon className="w-5 h-5 ml-2 text-gray-600" />
                قائمة المسافرين ({trip.passengers.length})
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        الاسم
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        رقم المقعد
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        رقم الجوال
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        حالة الصعود
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {trip.passengers.map((passenger, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {passenger.passengerName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {passenger.seatNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {passenger.phone || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            passenger.boardingStatus === 'boarded' ? 'bg-green-100 text-green-800' :
                            passenger.boardingStatus === 'no-show' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {passenger.boardingStatus === 'boarded' ? 'صعد' :
                             passenger.boardingStatus === 'no-show' ? 'لم يحضر' : 'مؤكد'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes */}
          {trip.notes && trip.notes.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">الملاحظات</h3>
              <div className="space-y-3">
                {trip.notes.map((note, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        note.type === 'departure' ? 'bg-blue-100 text-blue-800' :
                        note.type === 'arrival' ? 'bg-green-100 text-green-800' :
                        note.type === 'incident' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {note.type === 'departure' ? 'مغادرة' :
                         note.type === 'arrival' ? 'وصول' :
                         note.type === 'incident' ? 'حادث' : 'عام'}
                      </span>
                      <span className="text-sm text-gray-500">
                        <DateDisplay value={note.createdAt} showTime />
                      </span>
                    </div>
                    <p className="text-gray-900">{note.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
};

export default TripDetails; 